/**
 * Error kinds that must survive production redaction.
 *
 * Convex redacts a thrown `Error`'s message in production to a bare
 * "Server Error" — only a `ConvexError`'s structured `data` reaches the
 * browser. So any message a user is *meant* to read has to be thrown as a
 * `ConvexError` carrying one of these kinds. ADR 0017 in docs/DECISIONS.md is
 * the rule; these constants are its vocabulary.
 *
 * The kinds are here rather than in convex/ so the SPA can branch on them
 * without importing backend modules.
 */
export const USER_INPUT_ERROR_KIND = 'user_input_error';

/**
 * No Clerk session at all — expired, signed out, or a stale tab. Distinct
 * from `not_authorized` (a real session that just lacks the role/membership
 * for this trip or family): the fix here is "sign in again," not "ask an
 * admin." `requireIdentity` is the single most-called guard in convex/, so
 * this is the highest-traffic ADR-0017 kind — every action throws it.
 */
export const UNAUTHENTICATED_ERROR_KIND = 'unauthenticated';

/** The caller is signed in but this trip is not theirs, or no longer exists. */
export const TRIP_NOT_FOUND_ERROR_KIND = 'trip_not_found';

/**
 * The caller's family workspace reference is stale (deleted, or a
 * membership row that no longer resolves) — the family-scoped counterpart
 * to `trip_not_found`.
 */
export const FAMILY_NOT_FOUND_ERROR_KIND = 'family_not_found';

/** The caller's role on this trip is below what the action requires. */
export const NOT_AUTHORIZED_ERROR_KIND = 'not_authorized';

/**
 * An Ask Roam proposal was drafted against an older version of the trip.
 * Distinct from a plain input error because the UI can offer "ask Roam
 * again" rather than only printing text.
 */
export const STALE_SUGGESTION_ERROR_KIND = 'stale_suggestion';

/**
 * Server-side defense-in-depth re-classified an inline patch as above "low"
 * risk. This is not a failure: `packages/ai`'s chat-turn orchestrator catches
 * it by kind (never by message text — see chat-turn.ts's
 * `isPatchRiskReclassification`) and falls back to `proposePatch` so the user
 * still gets a confirmation card instead of a failed turn.
 */
export const PATCH_NEEDS_CONFIRMATION_ERROR_KIND = 'patch_needs_confirmation';
export const ASK_ROAM_PROTECTED_CHANGE_ERROR_KIND = 'ask_roam_protected_change';
export const ASK_ROAM_PROTECTED_CHANGE_MESSAGE =
  'This change may affect something you asked to keep. Nothing was changed; clarify which stops or days Roam may edit.';

/**
 * `AI_GENERATION_ENABLED` is off (a deliberate kill switch, not an outage).
 * Distinct kind so a caller can special-case it (e.g. `learnedPreferences`'s
 * distiller reports 'disabled' rather than 'provider_error') without
 * matching on the gate's message text, which the WS-G review found breaks
 * silently once a throw crosses a serialization boundary or is reworded.
 */
export const AI_GENERATION_DISABLED_ERROR_KIND = 'ai_generation_disabled';

export type KnownErrorKind =
  | typeof USER_INPUT_ERROR_KIND
  | typeof UNAUTHENTICATED_ERROR_KIND
  | typeof TRIP_NOT_FOUND_ERROR_KIND
  | typeof FAMILY_NOT_FOUND_ERROR_KIND
  | typeof NOT_AUTHORIZED_ERROR_KIND
  | typeof STALE_SUGGESTION_ERROR_KIND
  | typeof ASK_ROAM_PROTECTED_CHANGE_ERROR_KIND
  | typeof PATCH_NEEDS_CONFIRMATION_ERROR_KIND
  | typeof AI_GENERATION_DISABLED_ERROR_KIND;

/** Narrow an unknown thrown value to its structured kind, if it has one. */
export function errorKindOf(error: unknown): string | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const data = (error as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return undefined;
  const kind = (data as { kind?: unknown }).kind;
  return typeof kind === 'string' ? kind : undefined;
}

/** User-readable message from a thrown value, including structured ConvexError data. */
export function userFacingErrorMessage(error: unknown, fallback = 'Something went wrong.'): string {
  if (error && typeof error === 'object' && 'data' in error) {
    const data = (error as { data?: unknown }).data;
    if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
      const message = (data as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message.trim();
    }
    if (typeof data === 'string' && data.trim()) return data.trim();
  }
  if (error instanceof Error) {
    const trimmed = error.message.trim();
    if (trimmed.startsWith('{') && trimmed.includes('"message"')) {
      try {
        const parsed = JSON.parse(trimmed) as { message?: unknown };
        if (typeof parsed.message === 'string' && parsed.message.trim()) {
          return parsed.message.trim();
        }
      } catch {
        // fall through
      }
    }
    return trimmed || fallback;
  }
  return fallback;
}
